import * as d3 from 'd3';
import { ZOOM_SCALE_EXTENT } from '../utils/constants';
import type { ChartElements } from './types';

interface ZoomContext {
  plotWidth: number;
  plotHeight: number;
  elements: ChartElements;
  onZoom: (transform: d3.ZoomTransform) => void;
}

interface BrushContext {
  plotWidth: number;
  plotHeight: number;
  elements: ChartElements;
  getZoomBehavior: () => d3.ZoomBehavior<SVGGElement, unknown>;
}

export function setupZoomBehavior(ctx: ZoomContext): d3.ZoomBehavior<SVGGElement, unknown> {
  const zoomBehavior = d3
    .zoom<SVGGElement, unknown>()
    .scaleExtent(ZOOM_SCALE_EXTENT)
    .translateExtent([
      [0, 0],
      [ctx.plotWidth, ctx.plotHeight],
    ])
    .extent([
      [0, 0],
      [ctx.plotWidth, ctx.plotHeight],
    ])
    .filter((event) => {
      // Keep trackpad/page scrolling as the default. Require Shift+wheel to zoom.
      if (event?.type === 'wheel' && !event?.shiftKey) return false;
      if (event?.shiftKey && event?.type !== 'wheel') return false;
      return true;
    })
    .on('zoom', (event) => {
      ctx.onZoom(event.transform);
    });

  return zoomBehavior;
}

export function setupBrush(ctx: BrushContext): d3.BrushBehavior<unknown> {
  const brush = d3
    .brushX()
    .extent([
      [0, 0],
      [ctx.plotWidth, ctx.plotHeight],
    ])
    .filter((event) => event.shiftKey && !event.button)
    .on('end', (event) => {
      if (!event.selection) return;
      const [start, end] = event.selection as [number, number];
      if (Math.abs(end - start) < 4) {
        ctx.elements.interactionLayer.call(brush.move as any, null);
        return;
      }
      const scale = ctx.plotWidth / (end - start);
      const transform = d3.zoomIdentity.translate(-start, 0).scale(scale);
      ctx.elements.interactionLayer.call(ctx.getZoomBehavior().transform as any, transform);
      ctx.elements.interactionLayer.call(brush.move as any, null);
    });

  return brush;
}
